import React, { useState, useEffect, useRef } from "react";
import { auth, db } from "../../firebase";
import { doc, getDoc } from "firebase/firestore";
import OptionsHelpModal from "./OptionsHelpModal";

// --- 1. SHARED STYLES ---
const terminalInputStyle = {
  background: "rgba(0,0,0,0.3)",
  border: "1px solid rgba(80, 227, 194, 0.2)",
  color: "#50e3c2",
  padding: "12px",
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.8rem",
  outline: "none",
  width: "94%"
};

const sectionLabelStyle = {
  color: "#666",
  fontSize: "0.55rem",
  letterSpacing: "2px",
  marginBottom: "8px",
  display: "block"
};

// --- 2. CUSTOM SELECT COMPONENT (MOVED OUTSIDE) ---
const TerminalSelect = ({ name, placeholder, options, value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedLabel = options.find(opt => opt.value === value)?.label || placeholder;

  return (
    <div ref={containerRef} style={{ flex: 1, position: "relative" }}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        style={{
          ...terminalInputStyle,
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          userSelect: "none",
          width: "100%",
          boxSizing: "border-box"
        }}
      >
        <span style={{ color: value ? "#50e3c2" : "rgba(80, 227, 194, 0.5)" }}>
          {selectedLabel.toUpperCase()}
        </span>
        <span style={{ fontSize: "0.5rem", opacity: 0.5 }}>{isOpen ? "▲" : "▼"}</span>
      </div>

      {isOpen && (
        <div style={{
          position: "absolute",
          top: "100%",
          left: 0,
          right: 0,
          background: "#081212",
          border: "1px solid #50e3c2",
          zIndex: 100,
          marginTop: "2px",
          maxHeight: "200px",
          overflowY: "auto"
        }}>
          {options.map((opt) => (
            <div
              key={opt.value}
              onClick={() => {
                onChange({ target: { name, value: opt.value } });
                setIsOpen(false);
              }}
              style={{
                padding: "8px",
                fontSize: "0.7rem",
                color: value === opt.value ? "#000" : "#50e3c2",
                background: value === opt.value ? "#50e3c2" : "transparent",
                cursor: "pointer",
                fontFamily: "'JetBrains Mono', monospace"
              }}
            >
              {opt.label.toUpperCase()}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// --- 3. MAIN COMPONENT ---
export default function ContractCreator({ userData, onComplete }) {
  const [showHelp, setShowHelp] = useState(false);

  const [type, setType] = useState("Loan");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [globalTaxRate, setGlobalTaxRate] = useState(0);
  const [tickerPrice, setTickerPrice] = useState(0);
  const [tickerExists, setTickerExists] = useState(true);
  const [calculatedFee, setCalculatedFee] = useState(0);

  const [formValues, setFormValues] = useState({
    amount: 0, amount1: 0, amount2: 0, strikePrice: 0,
    interestRate: 0, fee: 0, term: "", expiry: "",
    ticker1: "", ticker2: "", ticker: "", recipient: ""
  });

  // Data Fetching: Global Vars
  useEffect(() => {
    const fetchGlobalVars = async () => {
      try {
        const docRef = doc(db, "Global_Vars", "Main");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) setGlobalTaxRate(docSnap.data().globalBaseTaxRate || 0);
      } catch (err) { console.error(err); }
    };
    fetchGlobalVars();
  }, []);

  // Data Fetching: Ticker Prices
  useEffect(() => {
    const fetchPrice = async () => {
      const activeTicker = type === "Exchange" ? formValues.ticker1 : formValues.ticker;
      const ticker = activeTicker?.toUpperCase();
      if (!ticker || ticker.length < 1 || type === "Loan") {
        setTickerPrice(0); setTickerExists(true); return;
      }
      try {
        const docRef = doc(db, "Companies", ticker);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setTickerPrice(docSnap.data().companyStockPrice || 0);
          setTickerExists(true);
        } else {
          setTickerPrice(0); setTickerExists(false);
        }
      } catch (err) { console.error(err); }
    };
    const timeoutId = setTimeout(fetchPrice, 500);
    return () => clearTimeout(timeoutId);
  }, [formValues.ticker1, formValues.ticker, type]);

  // Handle Input
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const isNumeric = ["amount", "interestRate", "strikePrice", "fee", "amount1", "amount2"].includes(name);
    setFormValues(prev => ({ ...prev, [name]: isNumeric ? parseFloat(value) || 0 : value }));
  };

  // Fee Calculation Logic
  useEffect(() => {
    const { amount, amount1, term, expiry } = formValues;
    if (globalTaxRate === 0 || !expiry) { setCalculatedFee(0); return; }
    const expiryMult = { "24Hr": 1.0, "7D": 1.2 }[expiry] || 1.0;
    let fee = 0;

    if (type === "Loan" && amount && term) {
      const termDiv = { "24Hr": 45, "7D": 15, "30D": 6 }[term] || 10;
      fee = (globalTaxRate * (Math.sqrt(amount) / termDiv)) * expiryMult;
    } else if (type === "Exchange" && amount1 && tickerPrice > 0) {
      fee = (globalTaxRate * (Math.sqrt(amount1 * tickerPrice) / 25)) * expiryMult;
    } else if (type === "Option" && amount && tickerPrice > 0 && term) {
      const termMult = { "24Hr": 1.0, "7D": 1.5, "30D": 2.5 }[term] || 1.0;
      fee = (globalTaxRate * (Math.sqrt(amount * tickerPrice) / 45)) * expiryMult * termMult;
    }
    setCalculatedFee(Math.round(fee));
  }, [formValues, type, globalTaxRate, tickerPrice]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!tickerExists && type !== "Loan") return;
    setLoading(true); setError("");

    const functionMap = {
      Loan: "https://us-central1-stockmarket-83a73.cloudfunctions.net/createContractLoan",
      Exchange: "https://us-central1-stockmarket-83a73.cloudfunctions.net/createContractExchange",
      Option: "https://us-central1-stockmarket-83a73.cloudfunctions.net/createContractOption",
    };

    try {
      const idToken = await auth.currentUser.getIdToken();
      const payload = { 
        ...formValues, 
        recipient: formValues.recipient || "Public", 
        ticker: formValues.ticker?.toUpperCase(), 
        ticker1: formValues.ticker1?.toUpperCase(), 
        ticker2: formValues.ticker2?.toUpperCase() 
      };
      const response = await fetch(functionMap[type], { 
        method: "POST", 
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` }, 
        body: JSON.stringify(payload) 
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Failed to issue contract.");
      onComplete();
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  const totalUpfrontCost = type === "Loan" ? (Number(formValues.amount) + calculatedFee) : calculatedFee;

  const ttlOptions = [{ value: "24Hr", label: "24 HOURS" }, { value: "7D", label: "7 DAYS" }];
  const termOptions = [{ value: "24Hr", label: "24 HOURS" }, { value: "7D", label: "7 DAYS" }, { value: "30D", label: "30 DAYS" }];

return (
  <div style={{ display: "flex", flexDirection: "column", height: "100%", fontFamily: "'JetBrains Mono', monospace", padding: "12px", boxSizing: "border-box", gap: "10px", background: "#050a0a", overflow: "hidden" }}>
    <form style={{ flex: 1, display: "flex", flexDirection: "column", gap: "10px", overflowY: "auto", overflowX: "hidden" }} onSubmit={handleSubmit}>
      {error && <div style={{ background: "rgba(255, 70, 70, 0.1)", color: "#ff4646", padding: "6px", border: "1px solid #ff4646", fontSize: "0.6rem" }}>{error.toUpperCase()}</div>}

      <div style={{ display: "flex", gap: "2px", flexShrink: 0 }}>
        {["Loan", "Exchange", "Option"].map((t) => (
          <button 
            type="button" 
            key={t} 
            onClick={() => { setType(t); setCalculatedFee(0); setTickerExists(true); }} 
            style={{ flex: 1, padding: "6px", cursor: "pointer", border: "1px solid rgba(80, 227, 194, 0.2)", background: type === t ? "rgba(80, 227, 194, 0.1)" : "transparent", color: type === t ? "#50e3c2" : "#444", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.65rem" }}
          >
            {t.toUpperCase()}
          </button>
        ))}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        {type === "Loan" && (
          <div style={{ display: "flex", justifyContent: "space-between", gap: "10px" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <span style={sectionLabelStyle}>PARAMETERS</span>
              <div style={{ display: "flex", gap: "6px" }}>
                <div style={{ flex: 1.5, minWidth: 0, display: "flex", flexDirection: "column", gap: "3px" }}>
                  <span style={{ fontSize: '0.45rem', color: '#444' }}>PRINCIPAL</span>
                  <input name="amount" type="number" placeholder="0.00" value={formValues.amount || ''} onChange={handleInputChange} required style={{...terminalInputStyle, padding: '5px 8px', width: '100%', boxSizing: 'border-box'}} />
                </div>
                <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "3px" }}>
                  <span style={{ fontSize: '0.45rem', color: '#444' }}>INT (%)</span>
                  <input name="interestRate" type="number" placeholder="0.00" value={formValues.interestRate || ''} onChange={handleInputChange} required style={{...terminalInputStyle, padding: '5px 8px', width: '100%', boxSizing: 'border-box'}} />
                </div>
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <span style={sectionLabelStyle}>TERMS</span>
              <div style={{ display: "flex", gap: "6px" }}>
                <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "3px" }}>
                  <span style={{ fontSize: '0.45rem', color: '#444' }}>REPAY</span>
                  <TerminalSelect name="term" placeholder="SELECT" options={termOptions} value={formValues.term} onChange={handleInputChange} />
                </div>
                <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "3px" }}>
                  <span style={{ fontSize: '0.45rem', color: '#444' }}>TTL</span>
                  <TerminalSelect name="expiry" placeholder="SELECT" options={ttlOptions} value={formValues.expiry} onChange={handleInputChange} />
                </div>
              </div>
            </div>
          </div>
        )}

        {type === "Exchange" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: "10px" }}>
              <div style={{ flex: 1, minWidth: 0, padding: "6px", background: "rgba(80, 227, 194, 0.02)", border: "1px solid rgba(80, 227, 194, 0.1)" }}>
                <span style={{...sectionLabelStyle, fontSize: '0.55rem'}}>OUTBOUND</span>
                <div style={{ display: "flex", gap: "6px", marginTop: "4px" }}>
                  <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "3px" }}>
                    <span style={{ fontSize: '0.45rem', color: '#444' }}>TICKER</span>
                    <input name="ticker1" type="text" placeholder="TICKER" value={formValues.ticker1 || ''} onChange={handleInputChange} required style={{ ...terminalInputStyle, padding: '5px 8px', width: '100%', boxSizing: 'border-box', borderColor: !tickerExists ? "#ff4646" : "rgba(80, 227, 194, 0.2)" }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "3px" }}>
                    <span style={{ fontSize: '0.45rem', color: '#444' }}>QTY</span>
                    <input name="amount1" type="number" placeholder="0" value={formValues.amount1 || ''} onChange={handleInputChange} required style={{...terminalInputStyle, padding: '5px 8px', width: '100%', boxSizing: 'border-box'}} />
                  </div>
                </div>
              </div>
              <div style={{ flex: 1, minWidth: 0, padding: "6px", background: "rgba(244, 197, 66, 0.02)", border: "1px solid rgba(244, 197, 66, 0.1)" }}>
                <span style={{ ...sectionLabelStyle, color: "#888", fontSize: '0.55rem' }}>INBOUND</span>
                <div style={{ display: "flex", gap: "6px", marginTop: "4px" }}>
                  <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "3px" }}>
                    <span style={{ fontSize: '0.45rem', color: '#444' }}>TICKER</span>
                    <input name="ticker2" type="text" placeholder="TICKER" value={formValues.ticker2 || ''} onChange={handleInputChange} required style={{ ...terminalInputStyle, padding: '5px 8px', width: '100%', boxSizing: 'border-box', color: "#F4C542" }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "3px" }}>
                    <span style={{ fontSize: '0.45rem', color: '#444' }}>QTY</span>
                    <input name="amount2" type="number" placeholder="0" value={formValues.amount2 || ''} onChange={handleInputChange} required style={{ ...terminalInputStyle, padding: '5px 8px', width: '100%', boxSizing: 'border-box', color: "#F4C542" }} />
                  </div>
                </div>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "3px", width: "140px" }}>
              <span style={{ fontSize: '0.45rem', color: '#444' }}>TIME TO LIVE</span>
              <TerminalSelect name="expiry" placeholder="SELECT" options={ttlOptions} value={formValues.expiry} onChange={handleInputChange} />
            </div>
          </div>
        )}

        {type === "Option" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: "10px" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={sectionLabelStyle}>COLLATERAL</span>
                <div style={{ display: "flex", gap: "6px" }}>
                  <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "3px" }}>
                    <span style={{ fontSize: '0.45rem', color: '#444' }}>TICKER</span>
                    <input name="ticker" type="text" placeholder="TICKER" value={formValues.ticker || ''} onChange={handleInputChange} required style={{...terminalInputStyle, padding: '5px 8px', width: '100%', boxSizing: 'border-box'}} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "3px" }}>
                    <span style={{ fontSize: '0.45rem', color: '#444' }}>QTY</span>
                    <input name="amount" type="number" placeholder="0" value={formValues.amount || ''} onChange={handleInputChange} required style={{...terminalInputStyle, padding: '5px 8px', width: '100%', boxSizing: 'border-box'}} />
                  </div>
                </div>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "4px" }}>
                  <span style={{ ...sectionLabelStyle, marginBottom: 0 }}>STRIKE & PREM</span>
                  <button type="button" onClick={() => setShowHelp(true)} style={{ background: "rgba(80, 227, 194, 0.1)", border: "1px solid rgba(80, 227, 194, 0.2)", color: "#50e3c2", cursor: "pointer", fontSize: "0.45rem", padding: "0px 3px" }}>[?]</button>
                </div>
                <div style={{ display: "flex", gap: "6px" }}>
                  <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "3px" }}>
                    <span style={{ fontSize: '0.45rem', color: '#444' }}>STRIKE ($)</span>
                    <input name="strikePrice" type="number" placeholder="0.00" value={formValues.strikePrice || ''} onChange={handleInputChange} required style={{...terminalInputStyle, padding: '5px 8px', width: '100%', boxSizing: 'border-box'}} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "3px" }}>
                    <span style={{ fontSize: '0.45rem', color: '#444' }}>FEE ($)</span>
                    <input name="fee" type="number" placeholder="0.00" value={formValues.fee || ''} onChange={handleInputChange} required style={{...terminalInputStyle, padding: '5px 8px', width: '100%', boxSizing: 'border-box'}} />
                  </div>
                </div>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: "10px" }}>
              <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "3px" }}>
                <span style={{ fontSize: '0.45rem', color: '#444' }}>TERM</span>
                <TerminalSelect name="term" placeholder="SELECT" options={termOptions} value={formValues.term} onChange={handleInputChange} />
              </div>
              <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "3px" }}>
                <span style={{ fontSize: '0.45rem', color: '#444' }}>TTL</span>
                <TerminalSelect name="expiry" placeholder="SELECT" options={ttlOptions} value={formValues.expiry} onChange={handleInputChange} />
              </div>
            </div>
          </div>
        )}

{type !== "option" && (
  <div style={{ display: "flex", flexDirection: "column", gap: "3px", width: "180px" }}>
    <span style={{ fontSize: '0.45rem', color: '#444' }}>RECIPIENT (OPTIONAL)</span>
    <input 
      name="recipient" 
      placeholder="PUBLIC" 
      value={formValues.recipient || ''} 
      onChange={handleInputChange} 
      style={{...terminalInputStyle, padding: '5px 8px', width: '100%', boxSizing: 'border-box'}} 
    />
  </div>
)}
      </div>
    </form>

    <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "auto", flexShrink: 0 }}>
      <div style={{ background: "rgba(0, 20, 20, 0.8)", border: "1px solid rgba(80, 227, 194, 0.2)", padding: "8px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", gap: "15px" }}>
          <div>
            <div style={{ fontSize: "0.55rem", color: "#444" }}>TYPE</div>
            <div style={{ fontSize: "0.6rem", color: "#50e3c2", fontWeight: "bold" }}>{type.toUpperCase()}</div>
          </div>
          <div>
            <div style={{ fontSize: "0.55rem", color: "#444" }}>TAX</div>
            <div style={{ fontSize: "0.8rem", color: "#ff4646" }}>-${calculatedFee.toLocaleString()}</div>
          </div>
        </div>
        <div style={{ textAlign: "right", borderLeft: "1px solid #1a2a2a", paddingLeft: "12px" }}>
          <div style={{ fontSize: "0.55rem", color: "#444" }}>UPFRONT</div>
          <div style={{ fontSize: "0.8rem", color: "#50e3c2", fontWeight: "bold" }}>${totalUpfrontCost.toLocaleString()}</div>
        </div>
      </div>

<button 
  type="submit"
  onClick={handleSubmit} 
  disabled={loading || !tickerExists} 
  style={{ 
    width: "100%", 
    padding: "10px", 
    background: "transparent",
    color: !tickerExists ? "#444" : "#50e3c2", 
    border: `1px solid ${!tickerExists ? "#222" : "rgba(80, 227, 194, 0.5)"}`, 
    
    fontFamily: "'JetBrains Mono', monospace", 
    fontWeight: "500",
    fontSize: "0.75rem", 
    textTransform: "uppercase",
    letterSpacing: "1px",
    cursor: !tickerExists ? "not-allowed" : "pointer",
    
    transition: "all 0.2s ease",
    borderRadius: "0px",
  }}
  onMouseEnter={(e) => {
    if (tickerExists && !loading) {
      e.target.style.background = "rgba(80, 227, 194, 0.1)";
      e.target.style.boxShadow = "0 0 15px rgba(80, 227, 194, 0.2)";
    }
  }}
  onMouseLeave={(e) => {
    e.target.style.background = "transparent";
    e.target.style.boxShadow = "none";
  }}
>
  {loading ? "PROCESSING..." : `CONFIRM ${type.toUpperCase()}`}
</button>
    </div>

    {showHelp && (
      <OptionsHelpModal 
        onClose={() => setShowHelp(false)} 
        formValues={formValues} 
        calculatedFee={calculatedFee} 
      />
    )}
  </div>
);
}